import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(undefined);

// ── Session timeout: 30 minutes of inactivity ──
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const MIN_REFRESH_INTERVAL_MS = 30 * 1000;

export const AuthProvider = ({ children }) => {
    // Modal open/view state
    const [modalState, setModalState] = useState({ isOpen: false, view: 'login' });

    // Real Supabase session state
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);

    // Subscription state — fetched from profiles table
    const [localIsPro, setLocalIsPro] = useState(false);
    const [subscriptionTier, setSubscriptionTier] = useState('free');

    const inactivityTimer = useRef(null);
    // Debounce tab-return session refreshes: don't hammer Supabase if user
    // switches tabs rapidly (e.g. Cmd+Tab back and forth).
    const lastRefreshRef = useRef(0);

    // ── Sign out (stable reference) ──
    const signOut = useCallback(async () => {
        await supabase.auth.signOut();
    }, []);

    // ── Inactivity timeout: auto-logout after 30 min of no activity ──
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(() => {
            console.warn('[SECURITY] Session timed out due to inactivity.');
            signOut();
        }, SESSION_TIMEOUT_MS);
    }, [signOut]);

    // Track user activity events to reset the timer
    useEffect(() => {
        // Only run timer when user is logged in
        if (!user) {
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
            return;
        }

        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        const handleActivity = () => resetInactivityTimer();

        // Start the timer
        resetInactivityTimer();

        // Listen for activity
        activityEvents.forEach(event =>
            window.addEventListener(event, handleActivity, { passive: true })
        );

        return () => {
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
            activityEvents.forEach(event =>
                window.removeEventListener(event, handleActivity)
            );
        };
    }, [user, resetInactivityTimer]);

    // ── Supabase auth listener ──
    useEffect(() => {
        // Load existing session on mount
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setUser(session?.user ?? null);
            setIsLoadingAuth(false);
        });

        // Listen for auth state changes (login, logout, token refresh)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            // NOTE: Do NOT call supabase.auth.startAutoRefresh() here.
            // supabase-js v2 manages auto-refresh internally via autoRefreshToken: true.
            // Calling startAutoRefresh() REMOVES the built-in visibilitychange handler
            // (_removeVisibilityChangedCallback), which breaks session recovery when
            // the user returns from a background tab — the root cause of the silent
            // dashboard freeze bug.
            // When user clicks password reset email link, open the reset view
            if (_event === 'PASSWORD_RECOVERY') {
                setModalState({ isOpen: true, view: 'resetPassword' });
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // ── Tab visibility & network recovery ──
    // supabase-js v2 handles token refresh on tab return via its internal
    // visibilitychange handler (_onVisibilityChanged → _recoverAndRefresh).
    // Our job here is just to ensure React state stays in sync with the
    // supabase client's internal session after the library has refreshed it.
    useEffect(() => {
        if (!user) return;

        let visibilityTimeout = null;

        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                // Clear any pending timeout (rapid tab switching)
                if (visibilityTimeout) clearTimeout(visibilityTimeout);

                const now = Date.now();
                if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS) {
                    return; // Debounce
                }
                lastRefreshRef.current = now;

                // Wait for supabase-js's internal handler to refresh the token first,
                // then sync React state with whatever session the client now holds.
                visibilityTimeout = setTimeout(async () => {
                    try {
                        const { data: { session: currentSession } } = await supabase.auth.getSession();
                        if (currentSession) {
                            setSession(currentSession);
                            setUser(currentSession.user);
                            console.log('[AuthContext] ✓ Session synced on tab return');
                        } else {
                            // Session was removed (expired, revoked, etc.)
                            console.warn('[AuthContext] No session found on tab return — signing out');
                            await supabase.auth.signOut();
                        }
                    } catch (err) {
                        console.error('[AuthContext] Session sync on tab return failed:', err);
                    }
                }, 1000); // 1s — let supabase-js refresh the token first
            }
        };

        // When user comes back online — verify session is still valid
        const handleOnline = async () => {
            console.log('[AuthContext] Network restored — verifying session...');
            try {
                const { data: { session: currentSession }, error } = await supabase.auth.getSession();
                if (!error && currentSession) {
                    setSession(currentSession);
                    console.log('[AuthContext] ✓ Session verified after coming online');
                }
            } catch (err) {
                console.error('[AuthContext] Failed to verify session after coming online:', err);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnline);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
            if (visibilityTimeout) clearTimeout(visibilityTimeout);
        };
    }, [user]);

    // ── Fetch subscription status from profiles table for current user ──
    useEffect(() => {
        const fetchSubscriptionStatus = async () => {
            if (!user) {
                setLocalIsPro(false);
                setSubscriptionTier('free');
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('is_pro, subscription_tier')
                    .eq('id', user.id)
                    .single();

                if (error) {
                    console.warn('Failed to fetch subscription status:', error);
                    setLocalIsPro(false);
                    setSubscriptionTier('free');
                    return;
                }

                if (data) {
                    const tier = data.subscription_tier || 'free';
                    const isPro = data.is_pro === true || tier === 'pro' || tier === 'business';
                    
                    setLocalIsPro(isPro);
                    setSubscriptionTier(tier);
                    
                    console.log('[AuthContext] ✓ Subscription loaded:', { 
                        tier, 
                        isPro,
                        proFlag: data.is_pro
                    });
                }
            } catch (err) {
                console.error('Error fetching subscription status:', err);
                setLocalIsPro(false);
                setSubscriptionTier('free');
            }
        };

        fetchSubscriptionStatus();
    }, [user]);

    // ── Modal helpers ──
    const openLogin = () => setModalState({ isOpen: true, view: 'login' });
    const openRegister = () => setModalState({ isOpen: true, view: 'register' });
    const closeModal = () => setModalState(prev => ({ ...prev, isOpen: false }));

    // ── Auth actions ──
    const signIn = async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (!error) closeModal();
        return { data, error };
    };

    const signUp = async (email, password, metadata = {}) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: metadata,
                emailRedirectTo: window.location.origin,
            },
        });
        return { data, error };
    };

    // ── Profile identity state (DOB, gender, username, change-limit info) ──
    const [profileIdentity, setProfileIdentity] = useState(null);

    const fetchProfileIdentity = useCallback(async () => {
        try {
            const { data, error } = await supabase.rpc('get_profile_identity');
            if (!error && data) setProfileIdentity(data);
        } catch { /* silent */ }
    }, []);

    // Fetch whenever user logs in / changes
    useEffect(() => {
        if (user) fetchProfileIdentity();
        else setProfileIdentity(null);
    }, [user, fetchProfileIdentity]);

    // ── Dashboard user mapping (replaces the old bridge AuthContext) ──
    // Maps the raw Supabase user to the shape dashboard components expect.
    const currentUser = useMemo(() => {
        if (!user) return null;
        return {
            id: user.id,
            name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'User',
            email: user.email,
            phone: user.user_metadata?.phone || '',
            occupation: user.user_metadata?.occupation || '',
            bio: user.user_metadata?.bio || '',
            security_question: user.user_metadata?.security_question || '',
            security_answer: user.user_metadata?.security_answer || '',
            currency: '$',
            dateFormat: 'MM/DD/YYYY',
            createdAt: user.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
            lastSignIn: user.last_sign_in_at || null,
            avatar: user.user_metadata?.avatar_url || null,
            isPro: user.user_metadata?.is_pro || localIsPro,
            // Identity fields — from DB (authoritative) or metadata fallback
            username: profileIdentity?.username || user.user_metadata?.username || user.email?.split('@')[0] || '',
            date_of_birth: profileIdentity?.date_of_birth || user.user_metadata?.date_of_birth || null,
            gender: profileIdentity?.gender || user.user_metadata?.gender || null,
            changesRemaining: profileIdentity?.changes_remaining ?? null,
            changeCount: profileIdentity?.change_count ?? null,
            windowEnd: profileIdentity?.window_end || null,
        };
    }, [user, localIsPro, profileIdentity]);

    // ── Dashboard-compatible aliases ──
    const logout = signOut;

    const updateProfile = async ({ full_name, avatar_url, phone, occupation, bio, security_question, security_answer } = {}) => {
        try {
            const updates = {};
            if (full_name !== undefined) updates.full_name = full_name;
            if (avatar_url !== undefined) updates.avatar_url = avatar_url;
            if (phone !== undefined) updates.phone = phone;
            if (occupation !== undefined) updates.occupation = occupation;
            if (bio !== undefined) updates.bio = bio;
            if (security_question !== undefined) updates.security_question = security_question;
            if (security_answer !== undefined) updates.security_answer = security_answer;

            // Update Supabase auth metadata
            const { error: metaError } = await supabase.auth.updateUser({ data: updates });
            if (metaError) return { success: false, error: metaError.message };

            // Sync to profiles table — includes security Q&A so forgot-password flow
            // always reads the latest values (get_security_question / verify_security_answer_get_email
            // both query the profiles table, not auth metadata).
            const profileUpdates = {};
            if (full_name !== undefined) profileUpdates.full_name = full_name;
            if (avatar_url !== undefined) profileUpdates.avatar_url = avatar_url;
            // Store answer lowercase — matches the case-insensitive compare used at signup
            if (security_question !== undefined) profileUpdates.security_question = security_question;
            if (security_answer !== undefined) profileUpdates.security_answer = security_answer.trim().toLowerCase();
            if (Object.keys(profileUpdates).length > 0) {
                await supabase.from('profiles').update(profileUpdates).eq('id', user.id);
            }

            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // ── Update Date of Birth and Gender (DB rate-limited to 3/30 days) ──
    const updateDobGender = async ({ date_of_birth, gender } = {}) => {
        try {
            const { data, error } = await supabase.rpc('update_dob_gender', {
                p_dob:    date_of_birth || null,
                p_gender: gender || null,
            });
            if (error) return { success: false, error: error.message };
            if (!data?.ok) return { success: false, error: 'Update failed' };
            // Refresh identity so UI reflects new count and values
            await fetchProfileIdentity();
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const changePassword = async (newPassword) => {
        try {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) return { success: false, error: error.message };
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const deleteAccount = async () => {
        try {
            // Get current session
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                return { success: false, error: "Not authenticated" };
            }

            // Call Edge Function directly via fetch — avoids any supabase-js wrapper issues
            const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-account`;
            const resp = await fetch(fnUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                    'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
                },
            });

            const data = await resp.json().catch(() => ({}));

            if (!resp.ok || data?.error) {
                return { success: false, error: data?.error ?? `HTTP ${resp.status}` };
            }

            // Clear local session after remote deletion
            await supabase.auth.signOut({ scope: 'local' });
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // ── Upgrade Action — redirect to pricing page ──
    const upgradeToPro = () => {
        window.location.href = '/pricing';
    };

    // ── Session Recovery for API failures ──
    // Called when an API returns 401/403 (unauthorized) — attempts to refresh the session
    // and returns the refreshed session if successful, or null if refresh fails
    const refreshSession = useCallback(async () => {
        console.log('[AuthContext] 🔄 Attempting to refresh session after API error...');
        try {
            const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
            if (error) {
                console.error('[AuthContext] ❌ Session refresh failed:', error.message);
                // Session can't be refreshed — user must re-authenticate
                await supabase.auth.signOut();
                return null;
            }
            if (newSession) {
                setSession(newSession);
                setUser(newSession.user);
                console.log('[AuthContext] ✅ Session refreshed successfully');
                return newSession;
            }
            return null;
        } catch (err) {
            console.error('[AuthContext] ❌ Session refresh exception:', err);
            return null;
        }
    }, []);

    return (
        <AuthContext.Provider value={{
            // Landing page API (useAuthModal consumers)
            modalState, openLogin, openRegister, closeModal,
            user, session, isLoadingAuth,
            signIn, signUp, signOut,

            // Dashboard API (useAuth consumers)
            currentUser,
            loading: isLoadingAuth,
            isAuthenticated: !!user,
            login: signIn,
            register: signUp,
            logout,
            updateProfile,
            updateDobGender,
            changePassword,
            deleteAccount,
            upgradeToPro,
            refreshSession,
            
            // Subscription status (NEW - for Priority Support + paid features)
            localIsPro,
            subscriptionTier,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * useAuthModal — Landing page hook (modal state + raw Supabase data).
 * Used by: Navbar, HeroSection, CTASection, AuthModal, App.js
 */
export const useAuthModal = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuthModal must be used within an AuthProvider');
    return context;
};

/**
 * useAuth — Dashboard hook (currentUser, logout, isAuthenticated).
 * Used by: ProtectedRoute, Navbar, Profile, Settings, FinanceContext
 *
 * This is the SAME context — just a semantic alias so dashboard code
 * can keep using `useAuth()` without any import changes needed.
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};
